import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  Typography,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useSelector } from '@xstate/react'
import { useContext, useState, useEffect } from 'react'
import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import styles from 'src/components/Dialogs/Payments/PaymentsDialog.scss'
import PrivacyAndTerms from 'src/components/Dialogs/Payments/common/PrivacyAndTerms'
import {
  getLmnMerchantName,
  getLmnStep,
  getLmnMerchantLogoUrl,
  getLmnNotQualified,
} from 'src/helpers/lmnHelpers'
import LmnPreliminaryAssessment from './Steps/LmnPreliminaryAssessment'
import LmnCandidatePage from './Steps/LmnCandidatePage'
import LmnHonestyAndPrivacy from './Steps/LmnHonestyAndPrivacy'
import LmnIntakeForm from './Steps/LmnIntakeForm'
import LmnPhoneVerification from './Steps/LmnPhoneVerification'
import LmnMedicalProfile from './Steps/LmnMedicalProfile'
import LmnIdentityVerification from './Steps/LmnIdentityVerification'
import LmnAllSet from './Steps/LmnAllSet'
import LmnDoesNotQualify from './Steps/LmnDoesNotQualify'

interface StepProps {
  preliminaryAssessmentProgress: number
  intakeFormProgress: number
  telehealthConsultationProgress: number
  completePurchaseProgress: number
  component: JSX.Element
}

const getStep = (lmnStep: number): StepProps => {
  const steps = [
    {
      preliminaryAssessmentProgress: 50,
      intakeFormProgress: 0,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnPreliminaryAssessment />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 14,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnCandidatePage />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 28,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnHonestyAndPrivacy />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 43,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnIntakeForm />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 56,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnPhoneVerification />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 71,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnMedicalProfile />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 85,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnIdentityVerification />,
    },
    {
      preliminaryAssessmentProgress: 100,
      intakeFormProgress: 100,
      telehealthConsultationProgress: 0,
      completePurchaseProgress: 0,
      component: <LmnAllSet />,
    },
  ]

  return steps[lmnStep]
}

const getMerchantName = (merchantName: string, merchantLogoUrl: string): JSX.Element => {
  const merchantNameUppercase = merchantName.toUpperCase()

  if (merchantLogoUrl && (merchantLogoUrl !== '')) {
    return (
      <Box
        display="flex"
        height="30px"
        sx={{
          margin: '10px',
        }}
      >
        <img src={merchantLogoUrl} alt={merchantNameUppercase} />

      </Box>
    )
  }
  // DEFAULT
  return (
    <Typography
      variant="h3"
      fontWeight="300"
      textAlign="center"
      color="primary.main"
      sx={{ textDecoration: 'none' }}
    >
      { merchantNameUppercase }
    </Typography>
  )
}

const LmnDialogContent = (): JSX.Element => {
  const { lmnContextService } = useContext(LmnContext)

  const lmnMerchantName = useSelector(lmnContextService, getLmnMerchantName)
  const lmnMerchantLogoUrl = useSelector(lmnContextService, getLmnMerchantLogoUrl)
  const lmnStep = useSelector(lmnContextService, getLmnStep)
  const lmnNotQualified = useSelector(lmnContextService, getLmnNotQualified)

  const { breakpoints } = useTheme()
  const isSmallViewport = useMediaQuery(breakpoints.down('sm'))
  const [preliminaryAssessmentProgress, setPreliminaryAssessmentProgress] = useState(0)
  const [intakeFormProgress, setIntakeFormProgresss] = useState(0)
  const [telehealthConsultationProgress, setTelehealthConsultationProgress] = useState(0)

  useEffect(() => {
    const step = getStep(lmnStep)
    if (step) {
      setPreliminaryAssessmentProgress(step.preliminaryAssessmentProgress)
      setIntakeFormProgresss(step.intakeFormProgress)
      setTelehealthConsultationProgress(step.telehealthConsultationProgress)
    }
  }, [lmnStep])

  return (
    <Box className={styles.paymentsDialogContent}>
      <Box
        className={styles.paymentsDialogCardContainer}
        padding={isSmallViewport ? '10px 16px' : '10px 24px'}
        width={isSmallViewport ? 'unset' : '457px'}
      >
        <Box className={styles.paymentsDialogMerchantName}>
          { lmnMerchantName ? (
            <Box display="flex" justifyContent="center">
              {getMerchantName(lmnMerchantName, lmnMerchantLogoUrl)}
            </Box>
          ) : null }
        </Box>
        {!lmnNotQualified ? (
          <Box sx={{ width: '100%', marginBottom: '20px' }}>
            <Box display="flex" gap="2px">
              <Box width="33.3%" textAlign="center">
                <LinearProgress
                  variant="determinate"
                  value={preliminaryAssessmentProgress}
                />
                <Typography variant="body4">
                  Preliminary Assessment
                </Typography>
              </Box>
              <Box width="33.3%" textAlign="center">
                <LinearProgress
                  variant="determinate"
                  value={intakeFormProgress}
                />
                <Typography variant="body4">
                  Intake Form
                </Typography>
              </Box>
              <Box width="33.3%" textAlign="center">
                <LinearProgress
                  variant="determinate"
                  value={telehealthConsultationProgress}
                />
                <Typography variant="body4">
                  Telehealth Consultation
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}
        <Card className={styles.paymentsDialogCard}>
          <CardContent>
            {lmnNotQualified ? <LmnDoesNotQualify /> : getStep(lmnStep).component}
          </CardContent>
        </Card>
        <PrivacyAndTerms />
      </Box>
    </Box>
  )
}

export default LmnDialogContent
